import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Key, Eye, EyeSlash, Warning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { validatePassword, PasswordRequirements } from '../utils/validatePassword';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteSignupPage() {
  const [params] = useSearchParams();
  const token = params.get('invite') || '';
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { loginWithTokens } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/invites/${token}`)
      .then(r => setInvite(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Ungültiger Einladungslink'));
  }, [token]);

  if (!token) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const pwdErr = validatePassword(password);
    if (pwdErr) return toast.error(pwdErr);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register-with-invite`, { invite_token: token, password }, { withCredentials: true });
      loginWithTokens(res.data);
      toast.success('¡Bienvenido/a! Dein Konto ist bereit.');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen canvas-spain flex items-center justify-center p-4" data-testid="invite-signup-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#AB1C24] to-[#8A151C] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Key size={32} className="text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#292524]">
            {invite?.name ? `¡Hola, ${invite.name}!` : 'Willkommen bei Habla'}
          </h1>
          {invite && (
            <p className="text-sm text-[#57534E] mt-2" data-testid="invite-signup-intro">
              {invite.invited_by ? `${invite.invited_by} hat dich eingeladen. ` : ''}Lege nur noch dein Passwort fest — danach bist du direkt drin.
            </p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
          {error ? (
            <div className="space-y-4 text-center" data-testid="invite-signup-error">
              <p className="text-sm text-red-600 flex items-center justify-center gap-2"><Warning size={18} />{error}</p>
              <Link to="/login"><Button className="w-full bg-[#AB1C24] hover:bg-[#8A151C]">Zum Login</Button></Link>
            </div>
          ) : !invite ? (
            <p className="text-center text-sm text-[#78716C]">Einladung wird geprüft…</p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-stone-50 rounded-lg px-3 py-2 col-span-2 sm:col-span-1 truncate" data-testid="invite-signup-email">
                  <span className="block text-[#A8A29E]">E-Mail</span>{invite.email}
                </div>
                <div className="bg-stone-50 rounded-lg px-3 py-2 col-span-2 sm:col-span-1" data-testid="invite-signup-level">
                  <span className="block text-[#A8A29E]">Niveau</span>{invite.spanish_level}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#292524] mb-1">Dein Passwort</label>
                <div className="relative">
                  <Input type={show ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Dein Passwort" required data-testid="invite-signup-password-input" />
                  <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C]" data-testid="invite-signup-toggle-password">
                    {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <PasswordRequirements password={password} />
              </div>
              <Button type="submit" disabled={loading || !!validatePassword(password)} className="w-full bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="invite-signup-submit-btn">
                {loading ? 'Konto wird erstellt…' : 'Konto erstellen & loslegen'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
